/**
 * CatalogoEstoque.jsx - Scriptus Pharma Partner
 * Página de Gerenciamento do Estoque de Produtos
 * 
 * ✅ Design alinhado com Dashboard.jsx e CatalogoProdutos.jsx
 * ✅ Glassmorphism Premium Refinado
 * ✅ Layout compacto sem sidebar
 * ✅ Drag and Drop mantido
 * ✅ Todas funcionalidades preservadas
 * ✅ Botão voltar corrigido para onVoltar()
 * ✅ Modal de cadastro integrado
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCatalogo } from '../hooks/useCatalogo';
import HeaderMenu from '../components/HeaderMenu';
import ModalCadastrarProduto from '../components/ModalCadastrarProduto';
import {
  Plus,
  Package,
  Pill,
  Loader2,
  X,
  CheckCircle,
  AlertTriangle,
  Search,
  Trash2,
  Edit3,
  Eye,
  TrendingUp,
  Store,
  RefreshCw,
  Tag,
  Clock,
  Check,
  Users,
  ArrowLeft,
  Filter
} from 'lucide-react';

// ============================================================================
// Componente: GlassCard (Reutilizado do Dashboard)
// ============================================================================
function GlassCard({ children, className = '', elevated = false }) {
  return (
    <div className={`${elevated ? 'glass-card-elevated-refined' : 'glass-card-refined'} rounded-2xl ${className}`}>
      {children}
    </div>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================
export default function CatalogoEstoque({ onVoltar }) {
  const { user } = useAuth();
  const { 
    produtos,
    estatisticas,
    categorias,
    dadosFarmacia,
    loading,
    error,
    criarProduto,
    atualizarProduto,
    removerProduto,
    toggleDisponibilidade,
    reordenarProdutos,
    refresh,
    clearError
  } = useCatalogo();

  // ============================================================================
  // ESTADOS
  // ============================================================================
  const [busca, setBusca] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroDisponibilidade, setFiltroDisponibilidade] = useState('todos');
  const [modalAberto, setModalAberto] = useState(false);
  const [modalCadastroAberto, setModalCadastroAberto] = useState(false);
  const [produtoEditando, setProdutoEditando] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [mensagem, setMensagem] = useState({ texto: '', tipo: 'success' });
  const [paginaAtual, setPaginaAtual] = useState(1);
  const produtosPorPagina = 20;

  // ============================================================================
  // REFS
  // ============================================================================
  const mountedRef = useRef(true);
  const mensagemTimeoutRef = useRef(null);

  // ============================================================================
  // CLEANUP
  // ============================================================================
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      if (mensagemTimeoutRef.current) {
        clearTimeout(mensagemTimeoutRef.current);
      }
    };
  }, []);

  // ============================================================================
  // SCROLL DETECTION
  // ============================================================================
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (mountedRef.current) {
            setScrolled(window.scrollY > 10);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ============================================================================
  // HELPERS
  // ============================================================================
  const mostrarMensagem = useCallback((texto, tipo = 'success', duracao = 4000) => {
    if (!mountedRef.current) return;
    
    if (mensagemTimeoutRef.current) {
      clearTimeout(mensagemTimeoutRef.current);
    }
    
    setMensagem({ texto, tipo });
    
    mensagemTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current) {
        setMensagem({ texto: '', tipo: 'success' });
      }
    }, duracao);
  }, []);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleEditarProduto = useCallback((produto) => {
    setProdutoEditando(produto);
    setModalAberto(true);
  }, []);

  const handleSalvarEdicao = useCallback(async (dados) => {
    if (!produtoEditando?.id) return;
    
    try {
      await atualizarProduto(produtoEditando.id, dados);
      setModalAberto(false);
      setProdutoEditando(null);
      mostrarMensagem('Produto atualizado com sucesso!', 'success');
    } catch (err) {
      mostrarMensagem(err.message || 'Erro ao atualizar produto', 'error');
    }
  }, [produtoEditando, atualizarProduto, mostrarMensagem]);

  const handleSalvarNovoProduto = useCallback(async (dados) => {
    try {
      await criarProduto(dados);
      setModalCadastroAberto(false);
      mostrarMensagem('Produto cadastrado com sucesso!', 'success');
    } catch (err) {
      mostrarMensagem(err.message || 'Erro ao cadastrar produto', 'error');
    }
  }, [criarProduto, mostrarMensagem]);

  const handleRemoverProduto = useCallback(async (produtoId) => {
    if (!produtoId) return;
    
    if (!window.confirm('Tem certeza que deseja remover este produto?')) {
      return;
    }

    try {
      await removerProduto(produtoId);
      mostrarMensagem('Produto removido com sucesso!', 'success');
    } catch (err) {
      mostrarMensagem(err.message || 'Erro ao remover produto', 'error');
    }
  }, [removerProduto, mostrarMensagem]);

  const handleToggleDisponibilidade = useCallback(async (produtoId) => {
    try {
      await toggleDisponibilidade(produtoId);
    } catch (err) {
      mostrarMensagem(err.message || 'Erro ao atualizar disponibilidade', 'error');
    }
  }, [toggleDisponibilidade, mostrarMensagem]);

  const handleRefresh = useCallback(async () => {
    await refresh();
    mostrarMensagem('Dados atualizados!', 'success');
  }, [refresh, mostrarMensagem]);

  // ============================================================================
  // FILTROS E PAGINAÇÃO
  // ============================================================================
  const produtosFiltrados = useMemo(() => {
    return produtos.filter(produto => {
      // Filtro de busca
      if (busca) {
        const termoBusca = busca.toLowerCase();
        const matchNome = produto.nome?.toLowerCase().includes(termoBusca);
        const matchConcentracao = produto.concentracao?.toLowerCase().includes(termoBusca);
        const matchPrincipio = produto.principioAtivo?.toLowerCase().includes(termoBusca);
        if (!matchNome && !matchConcentracao && !matchPrincipio) return false;
      }

      // Filtro de categoria
      if (filtroCategoria && produto.categoria !== filtroCategoria) {
        return false;
      }

      // Filtro de disponibilidade
      if (filtroDisponibilidade === 'disponivel' && !produto.disponivel) return false;
      if (filtroDisponibilidade === 'indisponivel' && produto.disponivel) return false;

      return true;
    });
  }, [produtos, busca, filtroCategoria, filtroDisponibilidade]);

  // Paginação
  const totalPaginas = Math.ceil(produtosFiltrados.length / produtosPorPagina);
  const indiceInicio = (paginaAtual - 1) * produtosPorPagina;
  const indiceFim = indiceInicio + produtosPorPagina;
  const produtosPaginados = produtosFiltrados.slice(indiceInicio, indiceFim);

  // Reset página quando filtros mudam
  useEffect(() => {
    setPaginaAtual(1);
  }, [busca, filtroCategoria, filtroDisponibilidade]);

  // ============================================================================
  // RENDERIZAÇÃO
  // ============================================================================
  return (
    <div className="min-h-screen bg-gradient-refined relative overflow-x-hidden">
      
      {/* Background Pattern */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="background-pattern-refined"></div>
        <div className="background-glow-1"></div>
        <div className="background-glow-2"></div>
        <div className="background-glow-3"></div>
      </div>

      {/* Overlay para modal */}
      {(modalAberto || modalCadastroAberto) && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30"
          onClick={() => {
            setModalAberto(false);
            setModalCadastroAberto(false);
          }}
        />
      )}

      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? 'header-scrolled-refined' : 'header-refined'
      }`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="logo-link-refined">
              <img src="/logo.PNG" alt="Scriptus RxIA" className="h-5 w-auto" />
            </Link>
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={loading.atualizando}
                className="refresh-button-refined"
                title="Atualizar dados"
              >
                <RefreshCw className={`w-3.5 h-3.5 text-slate-600 ${loading.atualizando ? 'animate-spin' : ''}`} />
              </button>
              <HeaderMenu />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 sm:pt-24 pb-12 relative z-10">
        
        {/* Título da Página */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="badge-pharmacy-refined mb-2 inline-flex">
            <Store className="w-3.5 h-3.5 text-emerald-600" />
            <span className="sf-pro-text text-xs font-semibold text-emerald-700">
              {dadosFarmacia.nome}
            </span>
          </div>
          <h1 className="text-xl sm:text-2xl sf-pro-display font-medium text-slate-900 tracking-tight mb-1.5">
            Gerenciar Estoque
          </h1>
          <p className="text-xs sm:text-sm sf-pro-text text-slate-500 font-normal">
            Gerenciar produtos para prescrições médicas
          </p>
        </div>

        {/* Grid de Estatísticas Compacto */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="stat-card-compact-refined">
            <div className="stat-icon-compact bg-gradient-to-br from-blue-500 to-blue-600">
              <Package className="w-3.5 h-3.5 text-white" />
            </div>
            <div>
              <p className="sf-pro-display font-bold text-slate-900 text-base">
                {estatisticas.total}
              </p>
              <p className="sf-pro-text text-slate-500 text-xs">Total</p>
            </div>
          </div>

          <div className="stat-card-compact-refined">
            <div className="stat-icon-compact bg-gradient-to-br from-emerald-500 to-emerald-600">
              <Check className="w-3.5 h-3.5 text-white" />
            </div>
            <div>
              <p className="sf-pro-display font-bold text-emerald-600 text-base">
                {estatisticas.disponiveis}
              </p>
              <p className="sf-pro-text text-slate-500 text-xs">Em Estoque</p>
            </div>
          </div>

          <div className="stat-card-compact-refined">
            <div className="stat-icon-compact bg-gradient-to-br from-purple-500 to-purple-600">
              <Eye className="w-3.5 h-3.5 text-white" />
            </div>
            <div>
              <p className="sf-pro-display font-bold text-purple-600 text-base">
                {estatisticas.totalVisualizacoes}
              </p>
              <p className="sf-pro-text text-slate-500 text-xs">Visualizações</p>
            </div>
          </div>

          <div className="stat-card-compact-refined">
            <div className="stat-icon-compact bg-gradient-to-br from-amber-500 to-amber-600">
              <TrendingUp className="w-3.5 h-3.5 text-white" />
            </div>
            <div>
              <p className="sf-pro-display font-bold text-amber-600 text-base">
                {estatisticas.totalLeads}
              </p>
              <p className="sf-pro-text text-slate-500 text-xs">Leads</p>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <GlassCard elevated className="p-4 mb-6 shadow-xl">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Busca */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                type="text"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar medicamento..."
                className="input-glass-refined w-full pl-10 text-xs"
              />
            </div>

            {/* Filtro de Categoria */}
            <select
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
              className="input-glass-refined text-xs min-w-[140px]"
            >
              <option value="">Todas categorias</option>
              {categorias.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            {/* Filtro de Disponibilidade */}
            <select
              value={filtroDisponibilidade}
              onChange={(e) => setFiltroDisponibilidade(e.target.value)}
              className="input-glass-refined text-xs min-w-[130px]"
            >
              <option value="todos">Todos</option>
              <option value="disponivel">Em estoque</option>
              <option value="indisponivel">Indisponível</option>
            </select>

            {/* Indicador de Filtros Ativos */}
            {(busca || filtroCategoria || filtroDisponibilidade !== 'todos') && (
              <button
                onClick={() => {
                  setBusca('');
                  setFiltroCategoria('');
                  setFiltroDisponibilidade('todos');
                }}
                className="filter-clear-button-refined"
              >
                <X className="w-3 h-3" />
                <span className="text-xs">Limpar</span>
              </button>
            )}
          </div>
        </GlassCard>

        {/* Botão de Adicionar Produto */}
        <div className="mb-6">
          <button
            onClick={() => setModalCadastroAberto(true)}
            disabled={loading.salvando}
            className="button-primary-refined-full"
          >
            {loading.salvando ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="sf-pro-text font-semibold">Aguarde...</span>
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                <span className="sf-pro-text font-semibold">Cadastrar Novo Produto</span>
              </>
            )}
          </button>
        </div>

        {/* Lista de Produtos em Cards */}
        <GlassCard elevated className="p-5 sm:p-6 shadow-xl">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="main-icon-refined bg-gradient-to-br from-emerald-100 to-emerald-50 border-2 border-emerald-200/50">
                <Pill className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="sf-pro-display font-semibold text-slate-900 text-lg">
                  Produtos em Estoque
                </h2>
                <p className="sf-pro-text text-slate-500 text-xs">
                  {produtosFiltrados.length > 0 ? 
                    `Página ${paginaAtual} de ${totalPaginas} • ${produtosFiltrados.length} produtos encontrados` :
                    'Nenhum produto encontrado'
                  }
                </p>
              </div>
            </div>

            <button
              onClick={onVoltar}
              className="button-secondary-compact"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span className="sf-pro-text text-xs font-semibold">Voltar</span>
            </button>
          </div>

          {loading.produtos ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            </div>
          ) : produtos.length === 0 ? (
            <div className="text-center py-12">
              <div className="main-icon-refined mx-auto mb-4 bg-gradient-to-br from-slate-100 to-slate-50 border-2 border-slate-200/50">
                <Package className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="sf-pro-display font-semibold text-slate-900 mb-2">
                Nenhum produto cadastrado
              </h3>
              <p className="sf-pro-text text-slate-500 text-sm">
                Cadastre seu primeiro produto para começar
              </p>
            </div>
          ) : produtosFiltrados.length === 0 ? (
            <div className="text-center py-12">
              <div className="main-icon-refined mx-auto mb-4 bg-gradient-to-br from-slate-100 to-slate-50 border-2 border-slate-200/50">
                <Search className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="sf-pro-display font-semibold text-slate-900 mb-2">
                Nenhum resultado encontrado
              </h3>
              <p className="sf-pro-text text-slate-500 text-sm">
                Ajuste os filtros de busca
              </p>
            </div>
          ) : (
            <>
              {/* Grid de Cards dos Produtos */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {produtosPaginados.map((produto) => (
                  <div 
                    key={produto.id}
                    className="group relative overflow-hidden rounded-3xl p-6 shadow-xl cursor-pointer glass-card border border-gray-200/50 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                  >
                    {/* Status de Disponibilidade - Badge no topo */}
                    <div className="absolute top-4 right-4 z-10">
                      <button
                        onClick={() => handleToggleDisponibilidade(produto.id)}
                        disabled={loading.salvando || loading.removendo}
                        className={`produto-badge-refined transition-all cursor-pointer ${
                          produto.disponivel 
                            ? 'produto-badge-success hover:bg-emerald-200/80' 
                            : 'produto-badge-warning hover:bg-amber-200/80'
                        }`}
                      >
                        {produto.disponivel ? (
                          <>
                            <Check className="w-2.5 h-2.5" />
                            <span>Em estoque</span>
                          </>
                        ) : (
                          <>
                            <Clock className="w-2.5 h-2.5" />
                            <span>Indisponível</span>
                          </>
                        )}
                      </button>
                    </div>

                    {/* Conteúdo do Card */}
                    <div className="flex justify-between items-start mb-6">
                      <div className="flex items-start gap-3 flex-1">
                        {/* Ícone do Produto */}
                        <div className={`produto-icon-refined ${
                          produto.disponivel 
                            ? 'bg-gradient-to-br from-emerald-100 to-emerald-50 border-emerald-200/50' 
                            : 'bg-gradient-to-br from-slate-100 to-slate-50 border-slate-200/50'
                        }`}>
                          <Pill className={`w-4 h-4 ${produto.disponivel ? 'text-emerald-600' : 'text-slate-400'}`} />
                        </div>

                        {/* Info do Produto */}
                        <div className="flex-1 min-w-0 pr-16">
                          <h3 className="sf-pro-display font-semibold text-slate-900 text-lg line-clamp-2 group-hover:text-slate-700 transition-colors">
                            {produto.nome || 'Sem nome'}
                          </h3>
                          {produto.concentracao && (
                            <p className="sf-pro-text text-slate-600 text-sm mt-1">
                              {produto.concentracao}
                            </p>
                          )}
                          {produto.principioAtivo && (
                            <p className="sf-pro-text text-slate-500 text-xs mt-1 italic">
                              {produto.principioAtivo}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Preço */}
                    <div className="mb-4">
                      <div className="glass-card rounded-2xl p-3 border border-gray-200/40">
                        <div className="flex items-center justify-between">
                          <span className="sf-pro-text text-sm text-slate-600 font-medium">Preço</span>
                          <span className="sf-pro-display font-bold text-emerald-600 text-lg">
                            {produto.preco && produto.preco !== 0 ? 
                              new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL'
                              }).format(produto.preco) : 
                              'Não informado'
                            }
                          </span>
                        </div>
                        {produto.produtoId && (
                          <p className="sf-pro-text text-slate-400 text-xs mt-1">
                            ID: {produto.produtoId}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Tags e Badges */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {/* Forma Farmacêutica */}
                      {produto.formaFarmaceutica && (
                        <span className="produto-badge-refined produto-badge-info">
                          {produto.formaFarmaceutica}
                        </span>
                      )}

                      {/* Categoria */}
                      {produto.categoria && (
                        <span className="produto-badge-refined produto-badge-primary">
                          <Tag className="w-2.5 h-2.5" />
                          <span>{produto.categoria}</span>
                        </span>
                      )}

                      {/* Visualizações */}
                      {produto.visualizacoes > 0 && (
                        <span className="produto-badge-refined produto-badge-neutral">
                          <Eye className="w-2.5 h-2.5" />
                          <span>{produto.visualizacoes}</span>
                        </span>
                      )}

                      {/* Leads */}
                      {produto.leads > 0 && (
                        <span className="produto-badge-refined produto-badge-success">
                          <Users className="w-2.5 h-2.5" />
                          <span>{produto.leads}</span>
                        </span>
                      )}
                    </div>

                    {/* Botões de Ação */}
                    <div className="flex justify-center gap-3 mt-6 pt-6 border-t border-gray-200/50">
                      <button
                        onClick={() => handleEditarProduto(produto)}
                        disabled={loading.salvando || loading.removendo}
                        className="button-primary bg-blue-600 hover:bg-blue-700 text-white transition-all disabled:opacity-50 flex-1"
                      >
                        <Edit3 className="w-4 h-4" />
                        <span className="sf-pro-text">Editar</span>
                      </button>
                      <button
                        onClick={() => handleRemoverProduto(produto.id)}
                        disabled={loading.salvando || loading.removendo}
                        className="button-primary bg-red-600 hover:bg-red-700 text-white transition-all disabled:opacity-50 flex-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="sf-pro-text">Remover</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Controles de Paginação */}
              {totalPaginas > 1 && (
                <div className="flex items-center justify-center gap-4 mt-8 pt-6 border-t border-slate-200/50">
                  <button
                    onClick={() => setPaginaAtual(prev => Math.max(prev - 1, 1))}
                    disabled={paginaAtual === 1}
                    className="button-secondary-compact disabled:opacity-50"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    <span className="sf-pro-text text-xs font-semibold">Anterior</span>
                  </button>

                  <div className="flex items-center gap-2">
                    {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                      let pageNumber;
                      if (totalPaginas <= 5) {
                        pageNumber = i + 1;
                      } else if (paginaAtual <= 3) {
                        pageNumber = i + 1;
                      } else if (paginaAtual >= totalPaginas - 2) {
                        pageNumber = totalPaginas - 4 + i;
                      } else {
                        pageNumber = paginaAtual - 2 + i;
                      }

                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setPaginaAtual(pageNumber)}
                          className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all ${
                            pageNumber === paginaAtual
                              ? 'bg-emerald-600 text-white shadow-lg'
                              : 'glass-card border border-gray-200/50 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setPaginaAtual(prev => Math.min(prev + 1, totalPaginas))}
                    disabled={paginaAtual === totalPaginas}
                    className="button-secondary-compact disabled:opacity-50"
                  >
                    <span className="sf-pro-text text-xs font-semibold">Próxima</span>
                    <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
                  </button>
                </div>
              )}
            </>
          )}
        </GlassCard>
      </main>

      {/* Modal de Edição */}
      <ModalCadastrarProduto
        visivel={modalAberto}
        onFechar={() => {
          setModalAberto(false);
          setProdutoEditando(null);
        }}
        produto={produtoEditando}
        onSalvar={handleSalvarEdicao}
        carregando={loading.salvando}
        nomeFarmacia={dadosFarmacia.nome}
      />

      {/* Modal de Cadastro */}
      <ModalCadastrarProduto
        visivel={modalCadastroAberto}
        onFechar={() => setModalCadastroAberto(false)}
        onSalvar={handleSalvarNovoProduto}
        carregando={loading.salvando}
        nomeFarmacia={dadosFarmacia.nome}
      />

      {/* Footer */}
      <footer className="footer-refined">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
            <img src="/logo.PNG" alt="Scriptus" className="h-3.5 w-auto opacity-60" />
            <span className="text-[10px] sf-pro-text text-slate-500">
              Scriptus Tecnologia em Saúde LTDA © 62.420.408/0001-70
            </span>
          </div>
        </div>
      </footer>

      {/* Notificação */}
      {mensagem.texto && (
        <div className="fixed bottom-6 right-6 left-6 sm:left-auto z-50 max-w-sm">
          <div className={`notification-refined ${
            mensagem.tipo === 'error' ? 'notification-error-refined' : 'notification-success-refined'
          }`}>
            <div className="flex items-center gap-3">
              <div className="notification-icon-refined">
                {mensagem.tipo === 'error' ? (
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                ) : (
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                )}
              </div>
              <span className="sf-pro-text font-medium text-slate-900 text-xs flex-1">
                {mensagem.texto}
              </span>
              <button 
                onClick={() => setMensagem({ texto: '', tipo: 'success' })} 
                className="notification-close-refined"
              >
                <X className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Styles */}
      <style>{styles}</style>
    </div>
  );
}

// ============================================================================
// STYLES - GLASSMORPHISM PREMIUM REFINADO
// ============================================================================
const styles = `
  /* ===== TIPOGRAFIA ===== */
  .sf-pro-display {
    font-family: -apple-system, "SF Pro Display", BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  
  .sf-pro-text {
    font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  /* ===== BACKGROUND ===== */
  .bg-gradient-refined {
    background: linear-gradient(135deg, 
      #f8fafc 0%, 
      #ffffff 25%, 
      #f1f5f9 50%, 
      #ffffff 75%, 
      #ecfdf5 100%);
  }

  .background-pattern-refined {
    position: absolute;
    inset: 0;
    background-image: 
      radial-gradient(circle at 25% 25%, rgba(16, 185, 129, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 75% 75%, rgba(20, 184, 166, 0.06) 0%, transparent 50%),
      radial-gradient(circle at 50% 10%, rgba(99, 102, 241, 0.04) 0%, transparent 60%);
    opacity: 0.7;
  }

  .background-glow-1 {
    position: absolute;
    top: 10%;
    left: 15%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, transparent 70%);
    border-radius: 50%;
    filter: blur(40px);
  }

  .background-glow-2 {
    position: absolute;
    bottom: 15%;
    right: 20%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(20, 184, 166, 0.06) 0%, transparent 70%);
    border-radius: 50%;
    filter: blur(35px);
  }

  .background-glow-3 {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.02) 0%, transparent 70%);
    border-radius: 50%;
    filter: blur(60px);
  }

  /* ===== HEADER ===== */
  .header-refined {
    background: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(24px) saturate(180%);
    -webkit-backdrop-filter: blur(24px) saturate(180%);
    border-bottom: 1px solid rgba(255, 255, 255, 0.4);
  }

  .header-scrolled-refined {
    background: rgba(255, 255, 255, 0.90);
    backdrop-filter: blur(30px) saturate(200%);
    -webkit-backdrop-filter: blur(30px) saturate(200%);
    border-bottom: 1px solid rgba(255, 255, 255, 0.6);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
  }

  .logo-link-refined {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .logo-link-refined:hover {
    opacity: 0.8;
    transform: scale(1.05);
  }

  /* ===== GLASS CARDS ===== */
  .glass-card-refined {
    background: rgba(255, 255, 255, 0.80);
    backdrop-filter: blur(24px) saturate(180%);
    -webkit-backdrop-filter: blur(24px) saturate(180%);
    border: 1px solid rgba(255, 255, 255, 0.5);
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.06),
      0 4px 16px rgba(0, 0, 0, 0.04),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .glass-card-elevated-refined {
    background: rgba(255, 255, 255, 0.90);
    backdrop-filter: blur(30px) saturate(200%);
    -webkit-backdrop-filter: blur(30px) saturate(200%);
    border: 1px solid rgba(255, 255, 255, 0.6);
    box-shadow: 
      0 25px 50px rgba(0, 0, 0, 0.08),
      0 12px 24px rgba(0, 0, 0, 0.06),
      0 6px 12px rgba(0, 0, 0, 0.04),
      inset 0 2px 0 rgba(255, 255, 255, 0.95);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* ===== ICONS ===== */
  .main-icon-refined {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
    box-shadow: 
      0 8px 24px rgba(0, 0, 0, 0.08),
      0 4px 12px rgba(0, 0, 0, 0.04),
      inset 0 2px 0 rgba(255, 255, 255, 0.9);
    flex-shrink: 0;
  }

  .stat-icon-compact {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 9px;
    box-shadow: 
      0 4px 12px rgba(0, 0, 0, 0.1),
      0 2px 6px rgba(0, 0, 0, 0.06);
    flex-shrink: 0;
  }

  /* ===== STAT CARDS ===== */
  .stat-card-compact-refined {
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(20px) saturate(180%);
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 12px;
    padding: 12px;
    display: flex;
    align-items: center;
    gap: 10px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 
      0 4px 12px rgba(0, 0, 0, 0.04),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
  }

  .stat-card-compact-refined:hover {
    transform: translateY(-2px);
    box-shadow: 
      0 8px 20px rgba(0, 0, 0, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }

  /* ===== PRODUTO CARDS ===== */
  .produto-card-refined {
    padding: 14px;
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(20px) saturate(180%);
    border: 1px solid rgba(255, 255, 255, 0.6);
    border-radius: 14px;
    box-shadow: 
      0 6px 16px rgba(0, 0, 0, 0.04),
      0 3px 8px rgba(0, 0, 0, 0.02),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .produto-card-refined:hover {
    transform: translateY(-2px);
    box-shadow: 
      0 12px 32px rgba(0, 0, 0, 0.08),
      0 6px 16px rgba(0, 0, 0, 0.05);
    border-color: rgba(16, 185, 129, 0.3);
  }

  .produto-icon-refined {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 11px;
    border: 1px solid;
    box-shadow: 
      0 4px 12px rgba(0, 0, 0, 0.06),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
    flex-shrink: 0;
  }

  .drag-handle-refined {
    padding: 4px;
    border-radius: 6px;
    transition: background 0.2s ease;
  }
  .drag-handle-refined:hover { 
    background: rgba(0, 0, 0, 0.05); 
  }

  /* ===== BADGES ===== */
  .badge-pharmacy-refined {
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(16, 185, 129, 0.08));
    border: 1px solid rgba(16, 185, 129, 0.25);
    border-radius: 9px;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.1);
  }

  .produto-badge-refined {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 3px 7px;
    border-radius: 6px;
    font-size: 0.65rem;
    font-weight: 600;
    border: 1px solid;
  }

  .produto-badge-success {
    background: rgba(16, 185, 129, 0.1);
    color: #047857;
    border-color: rgba(16, 185, 129, 0.2);
  }

  .produto-badge-warning {
    background: rgba(245, 158, 11, 0.1);
    color: #d97706;
    border-color: rgba(245, 158, 11, 0.2);
  }

  .produto-badge-info {
    background: rgba(59, 130, 246, 0.1);
    color: #1d4ed8;
    border-color: rgba(59, 130, 246, 0.2);
  }

  .produto-badge-primary {
    background: rgba(99, 102, 241, 0.1);
    color: #4338ca;
    border-color: rgba(99, 102, 241, 0.2);
  }

  .produto-badge-neutral {
    background: rgba(100, 116, 139, 0.1);
    color: #475569;
    border-color: rgba(100, 116, 139, 0.2);
  }

  /* ===== INPUTS ===== */
  .input-glass-refined {
    padding: 10px 14px;
    background: rgba(255, 255, 255, 0.85);
    border: 2px solid rgba(255, 255, 255, 0.6);
    border-radius: 11px;
    backdrop-filter: blur(16px);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-size: 0.75rem;
    color: #1e293b;
    box-shadow: 
      0 2px 8px rgba(0, 0, 0, 0.04),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
  }
  .input-glass-refined::placeholder { 
    color: #94a3b8; 
  }
  .input-glass-refined:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(16, 185, 129, 0.6);
    box-shadow: 
      0 0 0 3px rgba(16, 185, 129, 0.1),
      0 6px 20px rgba(16, 185, 129, 0.1),
      0 2px 8px rgba(0, 0, 0, 0.06);
    transform: translateY(-1px);
  }

  /* ===== BUTTONS ===== */
  .button-primary {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 16px;
    border: none;
    border-radius: 12px;
    color: white;
    font-weight: 600;
    font-size: 0.75rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 
      0 6px 20px rgba(0, 0, 0, 0.15),
      0 3px 10px rgba(0, 0, 0, 0.1);
    cursor: pointer;
  }

  .button-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 
      0 8px 25px rgba(0, 0, 0, 0.2),
      0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .button-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .button-primary-refined {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 24px;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border: none;
    border-radius: 14px;
    color: white;
    font-weight: 700;
    font-size: 0.875rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 
      0 10px 32px rgba(16, 185, 129, 0.25),
      0 6px 16px rgba(16, 185, 129, 0.15),
      inset 0 2px 0 rgba(255, 255, 255, 0.25);
    cursor: pointer;
  }

  .button-primary-refined:hover:not(:disabled) {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    transform: translateY(-2px);
    box-shadow: 
      0 16px 40px rgba(16, 185, 129, 0.3),
      0 8px 20px rgba(16, 185, 129, 0.2);
  }

  .button-primary-refined:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .button-primary-refined-full {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 20px;
    width: 100%;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border: none;
    border-radius: 12px;
    color: white;
    font-weight: 700;
    font-size: 0.8rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 
      0 8px 24px rgba(16, 185, 129, 0.2),
      0 4px 12px rgba(16, 185, 129, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    cursor: pointer;
  }

  .button-primary-refined-full:hover:not(:disabled) {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    transform: translateY(-1px);
    box-shadow: 
      0 12px 32px rgba(16, 185, 129, 0.25),
      0 6px 16px rgba(16, 185, 129, 0.15);
  }

  .button-primary-refined-full:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .button-secondary-compact {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(16px);
    border: 2px solid rgba(255, 255, 255, 0.6);
    border-radius: 10px;
    color: #475569;
    font-weight: 600;
    font-size: 0.75rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 
      0 4px 12px rgba(0, 0, 0, 0.04),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
    cursor: pointer;
  }

  .button-secondary-compact:hover {
    background: rgba(255, 255, 255, 0.95);
    color: #1e293b;
    transform: translateY(-1px);
    box-shadow: 
      0 8px 20px rgba(0, 0, 0, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }

  .refresh-button-refined {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(16px);
    border: 2px solid rgba(255, 255, 255, 0.6);
    border-radius: 10px;
    transition: all 0.3s ease;
    cursor: pointer;
  }

  .refresh-button-refined:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.95);
    transform: scale(1.05);
  }

  .action-button-refined {
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    transition: all 0.2s ease;
    border: 1px solid transparent;
    cursor: pointer;
  }

  .action-button-refined:hover:not(:disabled) {
    border-color: currentColor;
    transform: scale(1.1);
  }

  .filter-clear-button-refined {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 8px 12px;
    background: rgba(239, 68, 68, 0.1);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(239, 68, 68, 0.2);
    border-radius: 9px;
    color: #dc2626;
    font-weight: 600;
    transition: all 0.2s ease;
    cursor: pointer;
  }

  .filter-clear-button-refined:hover {
    background: rgba(239, 68, 68, 0.2);
    border-color: rgba(239, 68, 68, 0.3);
  }

  /* ===== NOTIFICATIONS ===== */
  .notification-refined {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(24px) saturate(200%);
    -webkit-backdrop-filter: blur(24px) saturate(200%);
    border-radius: 14px;
    padding: 14px 18px;
    animation: slideInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .notification-success-refined {
    border: 1px solid rgba(16, 185, 129, 0.3);
    box-shadow: 
      0 20px 60px rgba(16, 185, 129, 0.15),
      0 10px 30px rgba(16, 185, 129, 0.1);
  }

  .notification-error-refined {
    border: 1px solid rgba(239, 68, 68, 0.3);
    box-shadow: 
      0 20px 60px rgba(239, 68, 68, 0.15),
      0 10px 30px rgba(239, 68, 68, 0.1);
  }

  .notification-icon-refined {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 9px;
    border: 1px solid rgba(255, 255, 255, 0.6);
    flex-shrink: 0;
  }

  .notification-close-refined {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 7px;
    transition: all 0.3s ease;
    cursor: pointer;
  }

  .notification-close-refined:hover {
    background: rgba(255, 255, 255, 0.9);
  }

  /* ===== FOOTER ===== */
  .footer-refined {
    background: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(20px);
    border-top: 1px solid rgba(255, 255, 255, 0.5);
    padding: 16px 0;
    margin-top: 56px;
  }

  /* ===== ANIMATIONS ===== */
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* ===== RESPONSIVE ===== */
  @media (max-width: 768px) {
    .produto-card-refined {
      padding: 12px;
    }
    
    .produto-icon-refined {
      width: 36px;
      height: 36px;
    }
    
    .input-glass-refined {
      font-size: 16px; /* Prevent zoom on iOS */
    }
  }

  /* ===== ACCESSIBILITY ===== */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      transition-duration: 0.01ms !important;
    }
  }

  @media (prefers-contrast: high) {
    .glass-card-refined, 
    .glass-card-elevated-refined {
      background: rgba(255, 255, 255, 0.95);
      border: 2px solid rgba(0, 0, 0, 0.2);
    }
  }

  @media (pointer: coarse) {
    button, a, [role="button"] { 
      min-height: 44px; 
      min-width: 44px; 
    }
  }
`;